import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  Text,
  Button,
  TextInput,
  Layer,
  Form,
  FormField,
  Spinner,
} from "grommet";
import { Info, Search, Add } from "grommet-icons";
import { useNavigate, useParams } from "react-router-dom";
import adminAxios from "../../config/adminAxios";

import { HeaderNavigation } from "../../components/Headers/AdminHeader";
import { StickyFooter } from "../../components/Footers/GlobalFooter";
import CoursesTable from "../../components/AdminPortal/CoursesTable";
import BreadcrumbNavigation from "../../components/AdminPortal/BreadcrumbNavigation";
import AddCourseModal from "../../components/AdminPortal/AddCourseModal";
import QuestionsPopup from "../../components/AdminPortal/QuestionsPopup";

const ManageCoursesPage = () => {
  const { portfolio_id } = useParams();

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  // Search field state
  const [searchValue, setSearchValue] = useState("");

  // Edit Modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [showAddCourseModal, setShowAddCourseModal] = useState(false);
  const [portfolioName, setPortfolioName] = useState("");
  const [selectedCourseName, setSelectedCourseName] = useState("");

  // Fetch portfolio details (name) using the portfolio_id
  useEffect(() => {
    const fetchPortfolioName = async () => {
      try {
        const response = await adminAxios.get(`/api/admin/portfolio/${portfolio_id}`);
        const data = response.data;
        // Expected API output: { portfolio_name: "Compute" }
        setPortfolioName(data.portfolio_name);
      } catch (error) {
        console.error("Error fetching portfolio details:", error);
      }
    };

    if (portfolio_id) {
      fetchPortfolioName();
    }
  }, [portfolio_id]);

  useEffect(() => {
    const fetchCourses = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await adminAxios.get(`/api/admin/courses/${portfolio_id}`);
        setCourses(
          (response.data.courses || []).sort((a, b) =>
            a.name.localeCompare(b.name),
          ),
        );
      } catch (err) {
        setError("Unable to load courses. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    if (portfolio_id) {
      fetchCourses();
    }
  }, [portfolio_id]);

  // Full‑page loading
  if (loading) {
    return (
      <Box fill align="center" justify="center" background="light-2">
        <Spinner size="large" />
        <Text margin={{ top: "small" }}>Loading courses…</Text>
      </Box>
    );
  }

  // Full‑page error
  if (error) {
    return (
      <Box fill align="center" justify="center" background="light-2">
        <Text color="status-critical">{error}</Text>
      </Box>
    );
  }

  // Filter courses by search term (using course name)
  const filteredCourses = courses.filter((course) =>
    course.name?.toLowerCase().includes(searchValue.toLowerCase()),
  );

  // Edit button handler
  const handleEditCourse = (course) => {
    setSelectedCourse(course);
    setShowEditModal(true);
  };

  // Save (submit) handler for Edit Modal
  const handleEditSave = async (nextValue) => {
    if (!selectedCourse?.c_id) return;
    try {
      await adminAxios.put(`/api/courses/${selectedCourse.c_id}`, nextValue);
      // Update local state with new course data
      setCourses((prevCourses) =>
        prevCourses.map((c) =>
          c.c_id === selectedCourse.c_id ? { ...c, ...nextValue } : c,
        ),
      );
      setShowEditModal(false);
      setSelectedCourse(null);
    } catch (err) {
      alert("Failed to update course. Please try again.");
    }
  };

  const handleViewQuestions = (course) => {
    setSelectedCourseId(course.id);
    setSelectedCourseName(course.name);
  };

  const handleClosePopup = () => {
    setSelectedCourseId(null);
  };
  return (
    <Box fill="vertical" background="light-2">
      {/* Fixed Header Section */}
      <Box>
        <HeaderNavigation />
        <BreadcrumbNavigation
          breadcrumbs={[
            {
              label: "Actions",
              link: `/admin/portfolio/${portfolio_id}`,
            },
            {
              label: "Manage Courses",
              link: `/admin/portfolio/${portfolio_id}/manage-courses`,
            },
          ]}
        />
        {/* Brand line */}
        <Box
          height="2px"
          background="brand"
          margin={{ vertical: "small" }}
          width="100%"
        />
      </Box>

      {/* Main Content */}
      <Box flex pad="small">
        <Box
          background="#F2F2F2"
          elevation="small"
          round="small"
          pad="medium"
          gap="medium"
        >
          {/* Title + Info Icon + Intro Text */}
          <Box direction="row" align="center" gap="small">
            <Info size="medium" color="brand" />
            <Heading level="3" margin="none" color="brand" textAlign="start">
              Manage Courses
            </Heading>
          </Box>
          <Text>
            This page allows you to manage all courses within the portfolio—view
            detailed course info (name, skill, questions, vendor, duration,
            delivery type, level), add new courses, view questions and datasheet
            attached to the course, publish/unpublish courses, and use the
            search bar or action icons for quick access.
          </Text>

          {/* Page Header with Search & Add */}
          <Box
            direction="row"
            justify="between"
            align="center"
            margin={{ top: "small" }}
          >
            <Box direction="column" gap="xsmall">
              <Heading level="3" margin="none">
                {portfolioName} - Manage Courses
              </Heading>
              <Text>{filteredCourses.length} courses have been added.</Text>
            </Box>
            <Box direction="row" gap="small" align="center">
              <TextInput
                icon={<Search />}
                placeholder="Search course"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                width="300px"
              />
              <Button
                icon={<Add />}
                label="Add Course"
                secondary
                onClick={() => setShowAddCourseModal(true)}
                style={{ whiteSpace: "nowrap" }}
              />
            </Box>
          </Box>
          {selectedCourseId && (
            <QuestionsPopup
              courseId={selectedCourseId}
              onClose={handleClosePopup}
              courseName={selectedCourseName}
              portfolio_id={portfolio_id}
            />
          )}
          {/* Loading/Error or Courses Table */}
          {loading && (
            <Box align="center" justify="center" pad="medium">
              <Text>Loading courses...</Text>
            </Box>
          )}
          {error && !loading && (
            <Box align="center" justify="center" pad="medium">
              <Text color="status-critical">{error}</Text>
            </Box>
          )}
          {!loading && !error && (
            <Box round="small" overflow={{ horizontal: "auto" }}>
              <CoursesTable
                onViewQuestions={handleViewQuestions}
                courseData={filteredCourses}
                onEdit={handleEditCourse}
                portfolio_id={portfolio_id}
              />
            </Box>
          )}
        </Box>
      </Box>

      {/* Sticky Footer */}
      <StickyFooter />

      {showAddCourseModal && (
        <Layer
          onEsc={() => setShowAddCourseModal(false)}
          onClickOutside={() => setShowAddCourseModal(false)}
        >
          <AddCourseModal
            onClose={() => setShowAddCourseModal(false)}
            portfolio_id={portfolio_id}
          />
        </Layer>
      )}

      {/* Edit Course Modal */}
      {showEditModal && selectedCourse && (
        <Layer
          onEsc={() => setShowEditModal(false)}
          onClickOutside={() => setShowEditModal(false)}
        >
          <Box pad="medium" width="large" background="light-1" round="small">
            <Heading level={3} margin="none">
              Edit Course
            </Heading>
            <Form
              value={selectedCourse}
              onSubmit={({ value }) => handleEditSave(value)}
            >
              <FormField name="name" label="Course Name" required />
              <FormField name="Vendor" label="OEM" />
              <FormField name="duration" label="Duration" />
              <FormField name="course_order" label="Course Order" />
              <FormField name="level" label="Level" />

              <Box
                direction="row"
                gap="small"
                justify="end"
                margin={{ top: "medium" }}
              >
                <Button
                  label="Cancel"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedCourse(null);
                  }}
                />
                <Button type="submit" label="Save" primary />
              </Box>
            </Form>
          </Box>
        </Layer>
      )}
    </Box>
  );
};

export default ManageCoursesPage;
